import { styled } from 'styled-components'

import { FormattedMessage } from '@b-stock/fe-core/bstock-react'
import {
  Breakpoints,
  Colors,
  pxToRem,
  Typography,
} from '@b-stock/fe-core/design-system'

import { SimilarListingsButton } from './SimilarListingsButton'
import type {
  SimilarListingsProps,
  SimilarListingsVariant,
} from './useSearchSimilarListingsHandler'

type Props = Omit<SimilarListingsProps, 'variant' | 'screenName'> & {
  variant: 'sidebar' | 'section'
}

const Container = styled.div<{
  $variant: SimilarListingsVariant
}>`
  background-color: ${Colors.Semantic.Neutral.light_grey};
  border: ${pxToRem(1)} solid ${Colors.Semantic.Neutral.medium_grey};
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-top: ${(o) => (o.$variant !== 'sidebar' ? '0' : '1rem')};
  display: flex;
  flex-direction: ${(o) => (o.$variant === 'sidebar' ? 'column' : 'row')};
  justify-content: ${(o) =>
    o.$variant === 'section' ? 'space-between' : 'flex-start'};
  align-items: ${(o) => (o.$variant === 'section' ? 'center' : 'flex-start')};
  gap: ${(o) => (o.$variant === 'sidebar' ? '1rem' : '1.5rem')};
  width: 100%;

  @media ${Breakpoints.max.medium} {
    flex-direction: column;
    align-items: flex-start;
    margin-top: ${(o) => (o.$variant !== 'sidebar' ? '0' : '2rem')};
  }
`

const Text = styled.p<{ $variant: SimilarListingsVariant }>`
  ${(o) =>
    o.$variant === 'section' ? Typography.Title3 : Typography.Subtitle2}
  color: ${Colors.Semantic.Neutral.black};
  margin: 0;
  flex: ${(o) => (o.$variant === 'section' ? '1' : 'initial')};
`

/**
 * Search similar listings component.
 * - 'sidebar' variant: For right sidebar (variant B) - vertical layout
 * - 'section' variant: For above "More from this seller" (variant C) - horizontal layout
 */
export const SimilarListingsSectionOrSidebar = ({
  variant,
  searchQuery,
  condition,
  categories,
  promoteOtherListings,
  storefront,
}: Props) => {
  return (
    <Container
      $variant={variant}
      data-testid={`search-similar-listings-${variant}`}
    >
      <Text $variant={variant}>
        <FormattedMessage id="Listing.searchSimilar.notQuite" />
      </Text>
      <SimilarListingsButton
        searchQuery={searchQuery}
        condition={condition}
        categories={categories}
        promoteOtherListings={promoteOtherListings}
        storefront={storefront}
        variant={variant}
      />
    </Container>
  )
}
